import {EventEmitter} from "events";
import * as xml2js from "xml2js";
import objectAssign = require('object-assign');

export interface IAssetLoaderOptions
{
    basePath?: string;
    url: string;
    type?: string;
    crossOrigin?: boolean;
    blob?: boolean;
    log?: boolean;
    webAudioContext?: AudioContext;
    isTouchLocked?: boolean;
}

export interface IAsset
{
    url: string,
    file: any,
    type: string,
}

const DefaultOptions: IAssetLoaderOptions = {
    basePath: "",
    url: "",
    type: "",
    crossOrigin: false,
    blob: false,
    log: false,
    webAudioContext: null,
    isTouchLocked: false,
};

enum SUPPORTED_EXTENSIONS
{
    JSON = 'json',

    JPG = 'jpg',
    JPEG = 'jpeg',
    PNG = 'png',
    GIF = 'gif',
    WEBP = 'webp',
    SVG = 'svg',

    MP3 = 'mp3',
    OGG = 'ogg',
    OPUS = 'opus',
    WAV = 'wav',
    M4A = 'm4a',


    OGV = 'ogv',
    MP4 = 'mp4',
    WBM = 'webm',
    HLS = 'hls',

    BIN = 'bin',
    BINARY = 'binary',

    TXT = 'txt',
    TEXT = 'text',

    XML = 'xml',
}

export class AssetLoader extends EventEmitter
{
    static readonly EVENT_PROGRESS = "EVENT_PROGRESS";
    static readonly EVENT_COMPLETE = "EVENT_COMPLETE";
    static readonly EVENT_ERROR = "EVENT_ERROR";
    static readonly EVENT_DESTROY = "EVENT_DESTROY";

    private config: IAssetLoaderOptions;

    startTime: number;
    loadHandler: () => void;
    request: any;
    timeout: number;

    mbs: number = 0;
    secs: number = 0;

    constructor(options: IAssetLoaderOptions)
    {
        super();

        this.config = objectAssign({}, DefaultOptions, options);
        this.config.type = (this.config.type && this.config.type != "") ? this.config.type : AssetLoader.getExtension(this.config.url);
        this.config.type = this.config.type.toLowerCase();
        this.config.blob = (this.config.blob && AssetLoader.browserSupportsBlob);
    }

    start = (): void =>
    {
        this.startTime = Date.now();

        switch (this.config.type)
        {
            case SUPPORTED_EXTENSIONS.JSON:
                this.loadJSON();
                break;
            case SUPPORTED_EXTENSIONS.GIF:
            case SUPPORTED_EXTENSIONS.JPEG:
            case SUPPORTED_EXTENSIONS.JPG:
            case SUPPORTED_EXTENSIONS.PNG:
            case SUPPORTED_EXTENSIONS.WEBP:
            case SUPPORTED_EXTENSIONS.SVG:
                this.loadImage();
                break;
            case SUPPORTED_EXTENSIONS.MP3:
            case SUPPORTED_EXTENSIONS.OGG:
            case SUPPORTED_EXTENSIONS.OPUS:
            case SUPPORTED_EXTENSIONS.WAV:
            case SUPPORTED_EXTENSIONS.M4A:
                this.loadAudio();
                break;
            case SUPPORTED_EXTENSIONS.OGV:
            case SUPPORTED_EXTENSIONS.MP4:
            case SUPPORTED_EXTENSIONS.WBM:
            case SUPPORTED_EXTENSIONS.HLS:
                this.loadVideo();
                break;
            case SUPPORTED_EXTENSIONS.BIN:
            case SUPPORTED_EXTENSIONS.BINARY:
                this.loadBinary();
                break;
            case SUPPORTED_EXTENSIONS.TEXT:
            case SUPPORTED_EXTENSIONS.TXT:
                this.loadText();
                break;
            case SUPPORTED_EXTENSIONS.XML:
                this.loadXML();
                break;
            default:
                throw 'AssetLoader ERROR: Unknown type for file with URL: ' + this.config.basePath + this.config.url + ' (' + this.config.type + ')';
        }
    };

    private dispatchComplete = (data: any) =>
    {
        if (!data)
        {
            return;
        }
        const asset: IAsset = {
            url: this.config.url,
            file: data,
            type: this.config.type
        };
        this.emit(AssetLoader.EVENT_PROGRESS, 1);
        this.emit(AssetLoader.EVENT_COMPLETE, asset, asset.url, asset.type);
        this.removeListeners();
    };

    private loadXHR = (responseType: string, customLoadHandler) =>
    {
        this.loadHandler = customLoadHandler || this.completeHandler;

        this.request = new XMLHttpRequest();
        this.request.open('GET', this.config.basePath + this.config.url, true);
        this.request.responseType = responseType;
        this.request.addEventListener('progress', this.progressHandler);
        this.request.addEventListener('load', this.loadHandler);
        this.request.addEventListener('error', this.errorHandler);
        this.request.send();
    };

    private progressHandler = (event: ProgressEvent) =>
    {
        if (event.lengthComputable)
        {
            this.emit(AssetLoader.EVENT_PROGRESS, event.loaded / event.total);
        }
    };

    private completeHandler = () =>
    {
        if (this.success())
        {
            this.dispatchComplete(this.request.response);
        }
    };

    private success = (): boolean =>
    {
        // console.log('success', url, request.status);
        if (this.request && this.request.status < 400)
        {
            this.update();
            return true;
        }
        this.errorHandler(this.request && this.request.statusText);
        return false;
    };

    // JSON
    private loadJSON = (): void =>
    {
        this.loadXHR('json', () =>
        {
            if (this.success())
            {
                let data: any = this.request.response;
                if (typeof data === 'string')
                {
                    data = JSON.parse(data);
                }
                this.dispatchComplete(data);
            }
        });
    };

    // TXT
    private loadText = (): void =>
    {
        this.loadXHR('text', () =>
        {
            if (this.success())
            {
                let data: any = this.request.response;
                this.dispatchComplete(data);
            }
        });
    };

    // XML
    private loadXML = (): void =>
    {
        // must by text and not xml
        this.loadXHR('text', () =>
        {
            if (this.success())
            {
                let data: any = this.request.response;
                xml2js.parseString(data, (err: any, result: any) =>
                {
                    this.dispatchComplete(result);
                });
            }
        });
    };

    private loadBinary = (): void =>
    {
        this.loadXHR('arraybuffer', () =>
        {
            if (this.success())
            {
                let data: any = this.request.response;
                this.dispatchComplete(data);
            }
        });
    };


    // image

    private loadImage = (): void =>
    {
        if (this.config.blob)
        {
            this.loadImageBlob();
        }
        else
        {
            this.loadImageElement();
        }
    };

    private loadImageElement = (): void =>
    {
        this.request = new Image();
        if (this.config.crossOrigin)
        {
            this.request.crossOrigin = 'anonymous';
        }
        this.request.addEventListener('error', this.errorHandler, false);
        this.request.addEventListener('load', this.elementLoadHandler, false);
        this.request.src = this.config.basePath + this.config.url;
    };

    private elementLoadHandler = (event): void =>
    {
        window.clearTimeout(this.timeout);
        if (!event && (this.request.error || !this.request.readyState))
        {
            this.errorHandler(null);
            return;
        }
        this.dispatchComplete(this.request);
    };

    private loadImageBlob = (): void =>
    {
        this.loadXHR('blob', () =>
        {
            if (this.success())
            {
                this.request = new Image();
                this.request.addEventListener('error', this.errorHandler, false);
                this.request.addEventListener('load', this.imageBlobHandler, false);
                this.request.src = window.URL.createObjectURL(this.request.response);
            }
        });
    };

    private imageBlobHandler = (): void =>
    {
        window.URL.revokeObjectURL(this.request.src);
        this.dispatchComplete(this.request);
    };

    // audio
    private loadAudio = (): void =>
    {
        if (this.config.webAudioContext)
        {
            this.loadAudioBuffer();
        }
        else
        {
            this.loadMediaElement('audio');
        }
    };

    // video
    private loadVideo = (): void =>
    {
        if (this.config.blob)
        {
            this.loadXHR('blob', () =>
            {
            });
        }
        else
        {
            this.loadMediaElement('video');
        }
    };

    // audio buffer
    private loadAudioBuffer = () =>
    {
        this.loadXHR('arraybuffer', () =>
        {
            if (this.success())
            {
                this.config.webAudioContext.decodeAudioData(
                    this.request.response,
                    (buffer: any) =>
                    {
                        this.request = null;
                        this.dispatchComplete(buffer);
                    },
                    (e: Error) =>
                    {
                        this.errorHandler(e);
                    }
                );
            }
        });
    };

    // media element

    private loadMediaElement = (tagName: string) =>
    {
        this.request = document.createElement(tagName);

        if (!this.config.isTouchLocked)
        {
            // timeout because sometimes canplaythrough doesn't fire
            window.clearTimeout(this.timeout);
            this.timeout = window.setTimeout(this.elementLoadHandler, 2000);
            this.request.addEventListener('canplaythrough', this.elementLoadHandler, false);
        }

        this.request.addEventListener('error', this.errorHandler, false);
        this.request.preload = 'auto';
        this.request.src = this.config.basePath + this.config.url;
        this.request.load();

        if (this.config.isTouchLocked)
        {
            this.dispatchComplete(this.request);
        }
    };


    // error
    private errorHandler = (err: Error): void =>
    {
        // console.log('errorHandler', url, err);
        window.clearTimeout(this.timeout);

        let message: string = err.message;

        if (this.request && this.request.tagName && this.request.error)
        {
            const ERROR_STATE = ['', 'ABORTED', 'NETWORK', 'DECODE', 'SRC_NOT_SUPPORTED'];
            message = 'MediaError: ' + ERROR_STATE[this.request.error.code] + ' ' + this.request.src;
        }
        else if (this.request && this.request.statusText)
        {
            message = this.request.statusText;
        }
        else if (err && err.message)
        {
            message = err.message;
        }

        this.emit(AssetLoader.EVENT_ERROR, 'Error loading "' + this.config.basePath + this.config.url + '" ' + message);

        this.destroy();
    };

    // clean up

    private removeListeners = (): void =>
    {
        if (this.request)
        {
            this.request.removeEventListener('progress', this.progressHandler);
            this.request.removeEventListener('load', this.loadHandler);
            this.request.removeEventListener('error', this.errorHandler);
            this.request.removeEventListener('load', this.elementLoadHandler);
            this.request.removeEventListener('canplaythrough', this.elementLoadHandler);
            this.request.removeEventListener('load', this.imageBlobHandler);
        }
    };

    destroy = (): void =>
    {
        this.removeListeners();

        if (this.request && this.request.abort && this.request.readyState < 4)
        {
            this.request.abort();
        }

        this.request = null;
        this.config.webAudioContext = null;

        window.clearTimeout(this.timeout);

        this.emit(AssetLoader.EVENT_DESTROY, this.config.url);
    };


    // +++++++++++++++
    // --- statistics ---
    // +++++++++++++++

    private update = (): void =>
    {
        let length: number = 0;
        let headers = this.request.getAllResponseHeaders();
        if (headers)
        {
            const match = headers.match(/content-length: (\d+)/i);
            if (match && match.length)
            {
                length = parseInt(match[1], 10);
            }
        }

        if (!isNaN(length))
        {
            const mbs: number = length / 1024 / 1024;
            const secs: number = (Date.now() - this.startTime) / 1000;
            this.secs += secs;
            this.mbs += mbs;
            if (this.config.log)
            {
                this.log(this.config.url, mbs, secs);
            }
        }
        else if (this.config.log)
        {
            console.warn.call(console, 'Can\'t get Content-Length:', this.config.url);
        }
    };

    private log = (url: string, mbs: number, secs: number): void =>
    {
        if (url)
        {
            const file = 'File loaded: ' +
                url.substr(url.lastIndexOf('/') + 1) +
                ' size:' + mbs.toFixed(2) + 'mb' +
                ' time:' + secs.toFixed(2) + 's' +
                ' speed:' + (mbs / secs).toFixed(2) + 'mbps';

            console.log.call(console, file);
        }
        const total = 'Total loaded: ' + this.mbs.toFixed(2) + 'mb' +
            ' time:' + this.secs.toFixed(2) + 's' +
            ' speed:' + this.getMbps().toFixed(2) + 'mbps';
        console.log.call(console, total);
    };

    getMbps = (): number =>
    {
        return this.mbs / this.secs;
    };


    // +++++++++++++++
    // --- GETTERS & SETTERS ---
    // +++++++++++++++

    get url(): string
    {
        return this.config.url;
    }


    // +++++++++++++++
    // --- static methods ---
    // +++++++++++++++

    static getExtension = (url: string): string =>
    {
        return url && url.split('?')[0].split('.').pop().toLowerCase();
    };

    static loadFile(url: string, onSuccess: (data: any, url: string) => void, onError: (errorMessage: any, url: string) => void, onProgress: (evt: any) => void): void
    {
        let l: AssetLoader = new AssetLoader({url: url});
        l.start();
        l.addListener(AssetLoader.EVENT_COMPLETE, (evt) =>
        {
            onSuccess(evt.file, l.config.url);
            l.destroy();
        });
        l.addListener(AssetLoader.EVENT_ERROR, (errorMessage: string) =>
        {
            onError(errorMessage, l.config.url);
            l.destroy();
        });
        l.addListener(AssetLoader.EVENT_PROGRESS, (progress: number) =>
        {
            onProgress(progress);
        });
    }

    // +++++++++++++++
    // --- HELPERS ---
    // +++++++++++++++
    private static get browserSupportsBlob(): boolean
    {
        try
        {
            return !!new Blob();
        } catch (e)
        {
            return false;
        }
    }

    public static isSupportedFile(url:string):boolean
    {
        const EXT:string = url.split(".").pop();

        switch (EXT)
        {
            case SUPPORTED_EXTENSIONS.JSON:
            case SUPPORTED_EXTENSIONS.GIF:
            case SUPPORTED_EXTENSIONS.JPEG:
            case SUPPORTED_EXTENSIONS.JPG:
            case SUPPORTED_EXTENSIONS.PNG:
            case SUPPORTED_EXTENSIONS.WEBP:
            case SUPPORTED_EXTENSIONS.SVG:
            case SUPPORTED_EXTENSIONS.MP3:
            case SUPPORTED_EXTENSIONS.OGG:
            case SUPPORTED_EXTENSIONS.OPUS:
            case SUPPORTED_EXTENSIONS.WAV:
            case SUPPORTED_EXTENSIONS.M4A:
            case SUPPORTED_EXTENSIONS.OGV:
            case SUPPORTED_EXTENSIONS.MP4:
            case SUPPORTED_EXTENSIONS.WBM:
            case SUPPORTED_EXTENSIONS.HLS:
            case SUPPORTED_EXTENSIONS.BIN:
            case SUPPORTED_EXTENSIONS.BINARY:
            case SUPPORTED_EXTENSIONS.TEXT:
            case SUPPORTED_EXTENSIONS.TXT:
            case SUPPORTED_EXTENSIONS.XML:
                return true;
            default:
                return false;
        }
    }
}
